import DateInput from "@shared/components/DateInput";
import React, { use, useEffect, useMemo, useRef, useState } from "react";
import {
  useFormContext,
  useWatch,
  useFormState,
  Controller,
} from "react-hook-form";
import MultiSelectDropdown from "@pages/Program/components/MultiSelectDropdown";
import { useUserService } from "@utils/useUsers";
import { User } from "@shared/interfaces/User";
import CustomSelect from "@pages/Template/components/CustomSelect";
import { Donor } from "@shared/interfaces/ProgramFiles";
import { useDonor } from "@pages/Program/utils/useDonor";
import MultiSelectDonorDropdown from "@pages/Program/components/MultiSelectDonorDropdown";
import { handleCurrencyChange } from "@pages/Project/utils/utils";
import { useUser } from "@services/UserContext";
import EmojiPicker from "emoji-picker-react";
import { currencyService } from "@services/CurrencyService";
import { Currency, CurrencyOption } from "@shared/interfaces/ProjectsFiles";
import Select, { StylesConfig } from "react-select";

interface ProgramFormProps {
  isDisabled?: boolean;
  isEditing?: boolean;
  editPage?: boolean;
  children?: React.ReactNode;
  dataLoading?: boolean;
}

interface StatusMap {
  label: string;
  value: string;
}

const selectStyles: StylesConfig<CurrencyOption, false> = {
  control: (base) => ({
    ...base,
    backgroundColor: "white",
    minHeight: 40,
    height: 40,
    borderColor: "#D1D5DB",
    boxShadow: "none",
    fontSize: "0.875rem",
    "@media (min-width: 1536px)": {
      height: 48,
      minHeight: 48,
    },
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const ProgramForm: React.FC<ProgramFormProps> = ({
  isEditing,
  editPage,
  dataLoading,
  isDisabled,
}) => {
  const {
    register,
    setValue,
    control,
    trigger,
    watch,
    setError,
    clearErrors,
    getValues,
  } = useFormContext();

  const { user } = useUser();

  useEffect(() => {
    register("members", {
      validate: (members) => {
        return (
          members?.some((m: any) => m.leader) || "Program Leader is required"
        );
      },
    });
    register("donors");
  }, [register]);

  const { searchCompanyMembers } = useUserService();
  const { searchDonors } = useDonor();
  const [isHydrated, setIsHydrated] = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const icon = watch("icon") as string | undefined;

  const [allCompanyUsers, setAllCompanyUsers] = useState<User[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ user: User; leader: boolean }>
  >([]);

  const [leaders, setLeaders] = useState<User[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [leaderLoading, setLeaderLoading] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [allDonors, setAllDonors] = useState<Donor[]>([]);
  const [selectedDonors, setSelectedDonors] = useState<Donor[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const currency_id: number | null = watch("currency_id") ?? null;

  const prevSelectedUsersRef = useRef<Array<{ user: User; leader: boolean }>>(
    []
  );

  const selectedCurrency = useMemo(
    () => currencies.find((c) => c.id === currency_id) || null,
    [currencies, currency_id]
  );

  const currencyOptions = useMemo<CurrencyOption[]>(
    () =>
      currencies.map((c) => ({
        value: c.id,
        label: `${c.code}`,
        code: c.code,
        symbol: c.symbol,
      })),
    [currencies]
  );

  const statusOptions: StatusMap[] = [
    { label: "Active", value: "active" },
    { label: "Paused", value: "paused" },
  ];

  const fetchDonors = async (query: string): Promise<Donor[]> => {
    return await searchDonors(query);
  };

  const handleLeaderChange = (newLeaders: User[]) => {
    if (!user) return;

    const isSelfAllowed = !editPage || getValues("creator_id") === user.id;

    const updated = selectedUsers
      .map((u) => {
        const isNowLeader = newLeaders.some((l) => l.id === u.user.id);

        // Remove previous leaders unless it's self and allowed to stay as member
        if (u.leader && !isNowLeader) {
          if (u.user.id === user.id && isSelfAllowed) {
            return { ...u, leader: false }; // self becomes member
          }
          return null; // remove other leaders
        }

        return u;
      })
      .filter(Boolean) as typeof selectedUsers;

    const addedLeaders = newLeaders
      .filter((u) => {
        if (u.id === user.id && !isSelfAllowed) return false;
        return !updated.some((s) => s.user.id === u.id && s.leader === true);
      })
      .map((u) => ({
        user: u,
        leader: true,
      }));

    const cleanedMembers = updated.filter(
      (u) =>
        !addedLeaders.some((l) => l.user.id === u.user.id && l.leader === true)
    );

    setSelectedUsers([...cleanedMembers, ...addedLeaders]);
  };

  const handleMemberChange = (newMembers: User[]) => {
    const newList = selectedUsers.filter(
      (u) => u.leader && !newMembers.some((nm) => nm.id === u.user.id)
    );
    const updated = [
      ...newList,
      ...newMembers.map((u) => ({ user: u, leader: false })),
    ];
    setSelectedUsers(updated);
  };

  const fetchFilteredUsers = async (
    query: string,
    excludeIds: string[] = [],
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>
  ): Promise<(User & { full_name: string })[]> => {
    try {
      setLoading?.(true);
      const results = await searchCompanyMembers(query, true);
      return (results || [])
        .filter((user: User) => !excludeIds.includes(user.id))
        .map((user: User) => ({
          ...user,
          full_name: `${user.first_name} ${user.last_name}`,
        }));
    } finally {
      setLoading?.(false);
    }
  };

  const formMembers = useWatch({ name: "members", control });
  const formDonors = useWatch({ name: "donors", control });
  const { errors } = useFormState({
    control,
  });

  useEffect(() => {
    const prevUsers = prevSelectedUsersRef.current;

    const prevLeaders = new Set(
      prevUsers.filter((u) => u.leader).map((u) => u.user.id)
    );
    const prevMembers = new Set(
      prevUsers.filter((u) => !u.leader).map((u) => u.user.id)
    );

    const nextLeaders = selectedUsers
      .filter((u) => u.leader)
      .map((u) => u.user);
    const nextMembers = selectedUsers
      .filter((u) => !u.leader)
      .map((u) => u.user);

    const newLeaderIds = new Set(nextLeaders.map((u) => u.id));
    const newMemberIds = new Set(nextMembers.map((u) => u.id));

    const leaderChanged =
      prevLeaders.size !== newLeaderIds.size ||
      [...newLeaderIds].some((id) => !prevLeaders.has(id));
    const memberChanged =
      prevMembers.size !== newMemberIds.size ||
      [...newMemberIds].some((id) => !prevMembers.has(id));

    if (leaderChanged) setLeaderLoading(true);
    if (memberChanged) setMemberLoading(true);

    setLeaders(nextLeaders);
    setMembers(nextMembers);

    setTimeout(() => {
      if (leaderChanged) setLeaderLoading(false);
      if (memberChanged) setMemberLoading(false);
    }, 500);

    prevSelectedUsersRef.current = selectedUsers;
  }, [selectedUsers]);

  useEffect(() => {
    const fetchCompanyUsers = async (): Promise<void> => {
      try {
        const raw = await searchCompanyMembers("", true);
        const results: (User & { full_name: string })[] = (raw || []).map(
          (u: User) => ({
            ...u,
            full_name: `${u.first_name} ${u.last_name}`,
          })
        );
        setAllCompanyUsers(results);
      } catch (e) {
        console.error("Failed to fetch company users", e);
      }
    };

    const fetchDonorsData = async () => {
      const results = await searchDonors("");
      setAllDonors(results);
    };

    const fetchCurrencies = async () => {
      try {
        const response = await currencyService.getCurrencies();
        // Accept either { currencies: [...] } or bare array
        console.log(response);
        const items: Currency[] = Array.isArray(response.currencies)
          ? response.currencies
          : response?.currencies ?? [];
        setCurrencies(items);

        // Set default CHF if currency_id not set
        const current = getValues("currency_id");
        if (!current && items.length) {
          const chf = items.find((c) => c.code === "CHF") ?? items[0];
          setValue("currency_id", chf.id, { shouldValidate: true });
        }
      } catch (e) {
        console.error("Failed to fetch currencies", e);
      }
    };

    (async () => {
      try {
        setFetchingData(true);
        await fetchCompanyUsers();
      } catch (e) {
        console.error("Failed to fetch company users", e);
      } finally {
        setFetchingData(false);
      }

      try {
        setFetchingData(true);
        await fetchDonorsData();
      } catch (e) {
        console.error("Failed to fetch donors data", e);
      } finally {
        setFetchingData(false);
      }
    })();
    fetchCurrencies();
  }, [user]);

  // hydrate MEMBERS (only depends on users)
  useEffect(() => {
    if (!allCompanyUsers.length) return;

    const updatedSelectedUsers = (formMembers || [])
      .map(({ user_id, leader }: any) => {
        const matchedUser = allCompanyUsers.find((u) => u.id === user_id);
        return matchedUser ? { user: matchedUser, leader } : null;
      })
      .filter(Boolean) as Array<{ user: User; leader: boolean }>;

    setSelectedUsers((prev) => {
      const prevKey = prev.map((u) => `${u.user.id}-${u.leader}`).join(",");
      const nextKey = updatedSelectedUsers
        .map((u) => `${u.user.id}-${u.leader}`)
        .join(",");
      return prevKey !== nextKey ? updatedSelectedUsers : prev;
    });

    setIsHydrated(true);
  }, [formMembers, allCompanyUsers]);

  // hydrate DONORS (independent of users; do NOT gate on length)
  useEffect(() => {
    const matchedDonors = (formDonors || []).filter((d: Donor) =>
      allDonors.some((a) => a.id === d.id)
    );

    setSelectedDonors((prev) => {
      const prevIds = prev
        .map((u) => u.id)
        .sort()
        .join(",");
      const nextIds = matchedDonors
        .map((u: Donor) => u.id)
        .sort()
        .join(",");
      return prevIds !== nextIds ? matchedDonors : prev;
    });
  }, [formDonors, allDonors]);

  useEffect(() => {
    if (!isHydrated) return;

    const mapped = selectedUsers.map(({ user, leader }) => ({
      user_id: user.id,
      leader,
    }));
    setValue("members", mapped, { shouldValidate: true });

    requestAnimationFrame(() => {
      trigger("members");
    });
  }, [selectedUsers, isHydrated]);

  useEffect(() => {
    register("icon");
  }, [register]);

  // live validation
  const values = watch();

  return (
    <div className="flex gap-8 w-full">
      {/* Left Side */}
      <div className="w-1/2 space-y-4">
        {/* Program Name */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Program name <span className="text-red-600">*</span>
          </label>
          <div className="flex items-center space-x-2 relative">
            <input
              {...register("name", {
                required: "Program name is required",
              })}
              className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500 text-sm 2xl:text-base h-10 2xl:h-12"
              placeholder="Add a name"
              disabled={!isEditing || isDisabled}
            />

            <button
              type="button"
              className="border border-gray-300 rounded px-3 h-10 2xl:h-12 flex items-center justify-center hover:bg-gray-100"
              onClick={() => setShowEmojiPicker((v) => !v)}
              disabled={!isEditing || isDisabled}
              aria-label="Choose program icon"
            >
              {icon || (
                <img
                  src="/add_reaction.svg"
                  className="w-5 h-5 "
                  alt="Add reaction"
                />
              )}
            </button>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div className="absolute z-50  right-0 top-[45px]">
                <EmojiPicker
                  onEmojiClick={(e) => {
                    setValue("icon", e.emoji, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setShowEmojiPicker(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {errors.name && (
          <p className="text-red-500 text-xs 2xl:text-sm my-2">
            {String(errors.name.message)}
          </p>
        )}

        {/* Description */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Program description <span className="text-red-600">*</span>
          </label>
          <textarea
            {...register("description", {
              required: "Program description is required",
              maxLength: { value: 500, message: "Max 500 characters" },
            })}
            className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500 text-sm 2xl:text-base"
            rows={3}
            placeholder="Type anything..."
            disabled={!isEditing || isDisabled}
          />
          {errors.description && (
            <p className="text-red-500 text-xs 2xl:text-sm my-2">
              {String(errors.description.message)}
            </p>
          )}
        </div>

        {/* Budget */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Budget ({selectedCurrency?.symbol ?? "CHF"}){" "}
            <span className="text-red-600">*</span>
          </label>
          <div className="flex space-x-1">
            <div className="w-32 ">
              <Controller
                control={control}
                name="currency_id"
                rules={{ required: "Currency is required" }}
                render={({ field }) => (
                  <Select
                    inputId="currency_id"
                    instanceId="currency_id"
                    isDisabled={!isEditing || isDisabled}
                    options={currencyOptions}
                    className=""
                    value={
                      currencyOptions.find((o) => o.value === field.value) ??
                      null
                    }
                    onChange={(opt) =>
                      field.onChange(
                        (opt as CurrencyOption | null)?.value ?? null
                      )
                    }
                    placeholder=""
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                )}
              />
            </div>
            <input
              {...register("budget", {
                required: "Budget is required",
                pattern: {
                  value: /^[0-9]{1,3}(?:,[0-9]{3})*(?:\.\d{1,2})?$/,
                  message: "Invalid budget format (e.g., 1,000.50 or 1000.50)",
                },
                validate: {
                  maxValue: (value) => {
                    // Remove commas before converting to number
                    const numericValue = parseFloat(value.replace(/,/g, ""));
                    return (
                      numericValue <= 1e12 ||
                      "Budget must not exceed 10,000,000,000"
                    );
                  },
                },
              })}
              className="w-full border border-gray-300 rounded-md p-3 text-sm 2xl:text-base h-10 2xl:h-12 focus:outline-none focus:border-blue-500"
              placeholder="Example: 100.000"
              disabled={!isEditing || isDisabled}
              onChange={(e) =>
                handleCurrencyChange(
                  e,
                  "budget",
                  setValue,
                  clearErrors,
                  setError
                )
              }
            />
          </div>
          {errors.budget && (
            <p className="text-red-500 text-xs 2xl:text-sm my-2">
              {String(errors.budget.message)}
            </p>
          )}
        </div>

        {/* Budget Spent */}
        {editPage && (
          <div>
            <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
              Budget Spent({selectedCurrency?.symbol ?? "CHF"}){" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              {...register("budget_spent", {
                pattern: {
                  value: /^[0-9]{1,3}(?:,[0-9]{3})*(?:\.\d{1,2})?$/,
                  message: "Invalid budget format (e.g., 1,000.50 or 1000.50)",
                },
                validate: {
                  maxValue: (value) => {
                    const numericValue = parseFloat(value.replace(/,/g, ""));
                    return (
                      numericValue <= 1e12 ||
                      "Budget spent must not exceed 1,000,000,000,000"
                    );
                  },
                  lessThanBudget: (value) => {
                    const numericSpent = parseFloat(value.replace(/,/g, ""));
                    const numericBudget = parseFloat(
                      getValues("budget")?.replace(/,/g, "") || "0"
                    );

                    return (
                      numericSpent <= numericBudget ||
                      "Budget spent must be less than or equal to total budget"
                    );
                  },
                },
              })}
              className="w-full border border-gray-300 rounded-md p-3 text-sm 2xl:text-base h-10 2xl:h-12 focus:outline-none focus:border-blue-500"
              placeholder="Example: 100.000"
              disabled={!isEditing || isDisabled}
              // onChange={(e) => currencyFormat(e, "budget_spent")}
              onChange={(e) =>
                handleCurrencyChange(
                  e,
                  "budget_spent",
                  setValue,
                  clearErrors,
                  setError
                )
              }
            />
            {errors.budget_spent && (
              <p className="text-red-500 text-xs 2xl:text-sm my-2">
                {String(errors.budget_spent.message)}
              </p>
            )}
          </div>
        )}

        {/* Completion Percentage */}
        {editPage && (
          <div>
            <label className="block text-gray-700 mb-3">
              Completion (%) <span className="text-red-600">*</span>
            </label>
            <input
              type="number"
              {...register("completion_percentage", {
                required: "Completion percentage is required",
                min: { value: 0, message: "Cannot be less than 0%" },
                max: { value: 100, message: "Cannot be more than 100%" },
              })}
              className="w-full border border-gray-300 rounded-md p-3 text-sm 2xl:text-base h-10 2xlh-12 focus:outline-none focus:border-blue-500"
              placeholder="Example: 75"
              disabled={!isEditing || isDisabled}
            />
            {errors.completion_percentage && (
              <p className="text-red-500 text-xs 2xl:text-sm my-2">
                {String(errors.completion_percentage.message)}
              </p>
            )}
          </div>
        )}
        {/* End Date */}
        <div className="flex w-full space-x-4">
          <div className="w-1/2">
            {/* Start Date */}
            <div>
              <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
                Start date <span className="text-red-600">*</span>
              </label>
              <DateInput
                {...register("start_date", {
                  required: "Start Date is required",
                  validate: (value) =>
                    !values.end_date ||
                    new Date(value) <= new Date(values.end_date)
                      ? true
                      : "Start Date should be less than or equal to End Date",
                })}
                value={values.start_date}
                isEditing={isEditing}
                disabled={isDisabled}
              />
              {errors.start_date?.message && (
                <p className="text-red-500 text-xs 2xl:text-sm my-2">
                  {String(errors.start_date.message)}
                </p>
              )}
            </div>
          </div>
          <div className="w-1/2">
            <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
              End date <span className="text-red-600">*</span>
            </label>
            <DateInput
              {...register("end_date", {
                required: "End Date is required",
                validate: (value) =>
                  !values.start_date ||
                  new Date(value) >= new Date(values.start_date)
                    ? true
                    : "End Date should be greater than or equal to Start Date",
              })}
              value={values.end_date}
              isEditing={isEditing}
              disabled={isDisabled}
            />
            {errors.end_date && (
              <p className="text-red-500 text-xs 2xl:text-sm my-2">
                {String(errors.end_date.message)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div className="w-1/2 space-y-4">
        {/* Program leader_id */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Program leader <span className="text-red-600">*</span>
          </label>
          <MultiSelectDropdown
            selected={leaders}
            onChange={handleLeaderChange}
            fetchOptions={(q) =>
              fetchFilteredUsers(
                q,
                members
                  .filter((m) => {
                    // Exclude logged-in user only if they are not allowed to be leader
                    if (m.id === user?.id) {
                      return !(
                        !editPage || getValues("creator_id") === user.id
                      );
                    }
                    return true;
                  })
                  .map((m) => m.id),
                setLeaderLoading
              )
            }
            labelKey="full_name"
            valueKey="email"
            placeholder="Search users..."
            triggerDirty={() => false}
            showValueKey={true}
            loading={leaderLoading || dataLoading || fetchingData}
            singleSelect={true} // ✅ multi-leader support
            disabled={isDisabled}
          />
          {errors.members && (
            <p className="text-red-500 text-xs 2xl:text-sm my-2">
              {String(errors.members.message)}
            </p>
          )}
        </div>

        {/* Program Members */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Add program members
          </label>
          <MultiSelectDropdown
            selected={members}
            onChange={handleMemberChange}
            fetchOptions={(q) =>
              fetchFilteredUsers(
                q,
                leaders.map((l) => l.id),
                setMemberLoading
              )
            }
            labelKey="full_name"
            valueKey="email"
            placeholder="Search users..."
            triggerDirty={() => false}
            showValueKey={true}
            loading={memberLoading || dataLoading || fetchingData}
            disabled={isDisabled}
            enforceCurrentUserPresence={
              user && (!editPage || getValues("creator_id") === user.id)
                ? { currentUser: user, as: "member" }
                : undefined
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            After creating a program, invites will be sent to selected members.
          </p>
        </div>

        {/* Donors */}
        <div>
          <label className="block text-gray-700 mb-3">Donors</label>
          <MultiSelectDonorDropdown
            selected={selectedDonors}
            onChange={(newDonors) => {
              setSelectedDonors(newDonors);
              setValue("donors", newDonors, { shouldValidate: true });
            }}
            fetchOptions={fetchDonors}
            setAllDonors={setAllDonors}
            loading={dataLoading || fetchingData}
            disabled={isDisabled}
          />
        </div>

        {/* Status */}
        {editPage && (
          <div>
            <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
              Status <span className="text-red-600">*</span>
            </label>
            <CustomSelect
              name="status"
              options={statusOptions}
              placeholder="Select Status"
              disabled={!isEditing || isDisabled}
              useFormContext={useFormContext()}
            />
            {errors.status && (
              <p className="text-red-500 text-xs 2xl:text-sm my-2">
                {String(errors.status.message)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
    </div>
  );
};

export default ProgramForm;
